import awsExports from "../../aws-exports";
import { fetchAuthSession } from "aws-amplify/auth";

// Generic client for Lambdas fronted by the reusable REST API Gateway
// `lineupdashboardApiRest`. This replaces the per-Lambda hand-created console
// gateways (each with its own hardcoded `execute-api` URL). One gateway, one
// route per Lambda (see amplify/backend/api/lineupdashboardApiRest), all behind
// the shared Cognito User-Pool authorizer.
//
// The base URL comes from `aws_cloud_logic_custom` in aws-exports.js (populated
// by `amplify push`) — same resolution pattern as src/helpers/api/document.js.
// Route names are the plain path segment (e.g. "lineupdashboardApiBedrock");
// there is NO `-${env}` suffix — the gateway integration resolves the correct
// per-env Lambda ARN via CloudFormation.
const API_NAME = "lineupdashboardApiRest";

const API_BASE =
  awsExports.aws_cloud_logic_custom?.find((a) => a.name === API_NAME)?.endpoint ||
  "";

// Build a query string. Object-valued params are JSON-stringified (some Lambdas
// accept a JSON blob in a single query param, e.g. `?calculationPreValues={…}`).
const buildQueryString = (params = {}) => {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (entries.length === 0) return "";
  return (
    "?" +
    entries
      .map(([k, v]) => {
        const value = typeof v === "object" ? JSON.stringify(v) : v;
        return `${k}=${encodeURIComponent(value)}`;
      })
      .join("&")
  );
};

/**
 * Call a Lambda through the general REST API Gateway.
 *
 * @param {string} routeName  Path segment == the cli-inputs path without the
 *                            leading slash (e.g. "lineupdashboardApiBedrock").
 * @param {object} [opts]
 * @param {string} [opts.method="POST"]  HTTP method.
 * @param {object} [opts.query]          Query params (objects are JSON-stringified).
 * @param {*}      [opts.body]           Request body (objects are JSON.stringified).
 * @param {boolean}[opts.auth=true]      Send the Cognito idToken in Authorization.
 *                                       Set false for public routes (HsCodes,
 *                                       UserRegistration).
 * @param {object} [opts.headers]        Extra headers.
 * @param {boolean}[opts.raw=false]      Resolve the raw `Response` (caller reads
 *                                       status/parses). Otherwise parsed JSON is
 *                                       returned and a non-2xx throws.
 * @returns {Promise<*>}  Parsed JSON (or the raw Response when `raw`).
 */
export async function callLambda(
  routeName,
  { method = "POST", query, body, auth = true, headers = {}, raw = false } = {},
) {
  const url = `${API_BASE}/${routeName}${buildQueryString(query)}`;

  const finalHeaders = { "Content-Type": "application/json", ...headers };
  if (auth) {
    finalHeaders.Authorization = (await fetchAuthSession()).tokens?.idToken?.toString();
  }

  const res = await fetch(url, {
    method,
    headers: finalHeaders,
    ...(body !== undefined
      ? { body: typeof body === "string" ? body : JSON.stringify(body) }
      : {}),
  });

  if (raw) return res;

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    const message =
      (typeof data === "object" && (data?.message || data?.error)) ||
      (typeof data === "string" ? data : null);
    const err = new Error(message || `API ${routeName} failed: ${res.status}`);
    err.status = res.status;
    err.data = data;
    err.code = typeof data === "object" ? data?.code : undefined;
    throw err;
  }
  return data;
}
